import React, { useRef, useState } from 'react'
import Neon from '@/features/arcade/components/ArcadeOverview/Neon'
import {
  motion,
  useMotionValueEvent,
  useScroll,
  useTransform
} from 'framer-motion'
import Image from 'next/image'
import { classNames } from '@/util/tailwindHelpers'
import { ButtonLink } from '@/components/Button'
import Icon from '@/components/Icon'
import useBreakpoints from '@/hooks/useBreakpoints'
import { SOCIAL_LINKS } from '@/constants'
import SocialShareMenu from '@/components/SocialShareMenu'
import CircleIconLink from '@/components/CircleIconLink'
import SeasonStartCountdownBadge from '@/features/arcade/components/SeasonStartCountdownBadge'
import CharacterGroup from './CharacterGroup'
import { OVERVIEW_PAGE_SECTION_ID } from '@/features/arcade/hooks/routing'
import Link from 'next/link'
import { getPrimaryButton } from '@/features/arcade/helpers'
import { MergedSeason } from '@/features/arcade/types'

const LOGO_TITLE_VARIANTS = {
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  hidden: { opacity: 0, y: 10, transition: { duration: 0.5 } }
}

const SCROLL_ARROW_VARIANTS = {
  start: {
    opacity: 1,
    transition: { duration: 0.5 },
    display: 'block'
  },
  end: {
    opacity: 0,
    transition: { duration: 0.5 },
    display: 'block'
  }
}

const ACTIONS_VARIANTS = {
  start: { opacity: 0, transition: { duration: 0.5 } },
  end: { opacity: 1, transition: { duration: 0.5 } }
}

type Props = {
  season: MergedSeason
  active: MergedSeason
}

const ArcadeOverviewHero = ({ season, active }: Props) => {
  const { isSmall, isMedium, isLarge, isMobile } = useBreakpoints([
    'sm',
    'md',
    'lg',
    'mobile'
  ])

  const scenePlaceholderRef = useRef<HTMLDivElement>(null)
  const [isArrowHidden, setIsArrowHidden] = useState(false)
  const [areActionsHidden, setAreActionsHidden] = useState(false)
  const [isTitle1Hidden, setIsTitle1Hidden] = useState(true)
  const [isTitle2Hidden, setIsTitle2Hidden] = useState(true)
  const [isTitle3Hidden, setIsTitle3Hidden] = useState(true)

  const { scrollYProgress } = useScroll({
    target: scenePlaceholderRef,
    offset: ['start start', 'end end']
  })
  const { scrollYProgress: ScrollYProgressEnd } = useScroll({
    target: scenePlaceholderRef,
    offset: ['start start', 'end start']
  })

  let settings = {
    zTransformValues: ['65vh', '300vh'],
    logoTransformValues: [0.65, 0.9],
    perspective: 2500,
    groupXSpread: '24vw',
    characterZSpread: '-75vw',
    characterXOffset: '-4vw',
    cameraZ: '400px',
    groupZ: '500px',
    cameraY: '40vw'
  }

  if (isSmall) {
    settings = {
      zTransformValues: ['65vh', '300vh'],
      logoTransformValues: [0.65, 0.9],
      perspective: 2500,
      groupXSpread: '24vw',
      characterZSpread: '-75vw',
      characterXOffset: '-4vw',
      cameraZ: '400px',
      groupZ: '500px',
      cameraY: '40vw'
    }
  }

  if (isMedium) {
    settings = {
      zTransformValues: ['65vh', '300vh'],
      logoTransformValues: [0.75, 1],
      perspective: 2500,
      groupXSpread: '24vw',
      characterZSpread: '-75vw',
      characterXOffset: '-4vw',
      cameraZ: '400px',
      groupZ: '500px',
      cameraY: '32vw'
    }
  }

  if (isLarge) {
    settings = {
      zTransformValues: ['45vw', '230vw'],
      logoTransformValues: [0.75, 1],
      perspective: 2500,
      groupXSpread: '26vw',
      characterZSpread: '-30vw',
      characterXOffset: '-2vw',
      cameraZ: '0px',
      groupZ: '0px',
      cameraY: '24vw'
    }
  }

  const z = useTransform(scrollYProgress, [0, 1], settings.zTransformValues)
  const logoScale = useTransform(
    ScrollYProgressEnd,
    [0, 0.8],
    settings.logoTransformValues
  )

  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (latest >= 0.8 && !isArrowHidden) {
      setIsArrowHidden(true)
    } else if (latest < 0.8 && isArrowHidden) {
      setIsArrowHidden(false)
    }

    if (latest >= 1 && !areActionsHidden) {
      setAreActionsHidden(true)
    } else if (latest < 1 && areActionsHidden) {
      setAreActionsHidden(false)
    }

    if (latest >= 0.3 && isTitle1Hidden) {
      setIsTitle1Hidden(false)
    } else if (latest < 0.3 && !isTitle1Hidden) {
      setIsTitle1Hidden(true)
    }

    if (latest > 0.5 && isTitle2Hidden) {
      setIsTitle2Hidden(false)
    } else if (latest < 0.5 && !isTitle2Hidden) {
      setIsTitle2Hidden(true)
    }

    if (latest > 0.8 && isTitle3Hidden) {
      setIsTitle3Hidden(false)
    } else if (latest < 0.8 && !isTitle3Hidden) {
      setIsTitle3Hidden(true)
    }
  })

  const hasMeasurements = isSmall || isMedium || isLarge || isMobile

  const primaryButton = getPrimaryButton(!!active)

  return (
    <div
      className={classNames(
        '-mt-[calc(var(--headerHeight)+var(--secondaryNavHeight))] flex flex-col justify-start relative z-10 mb-8 md:mb-0 lg:mb-16'
      )}
    >
      {/* Character Layout */}
      <div
        className={classNames(
          'fixed inset-0 flex overflow-hidden items-center pointer-events-none z-10 transition-opacity',
          { 'opacity-0': !hasMeasurements || areActionsHidden }
        )}
        style={{
          perspective: settings.perspective,
          transformStyle: 'preserve-3d',
          '--groupXSpread': settings.groupXSpread,
          '--groupZ': settings.groupZ,
          '--characterZSpread': settings.characterZSpread,
          '--characterXOffset': settings.characterXOffset,
          '--cameraZ': '0px',
          '--cameraY': settings.cameraY
        }}
      >
        <motion.div
          className="flex-1 relative h-0"
          style={{
            z,
            translateY: 'var(--cameraY)',
            translateX: '.5vw',
            transformStyle: 'preserve-3d'
          }}
        >
          <div
            className="absolute top-0 left-1/2 w-[200vw] h-[200vw] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-[50vw] after:bg-gradient-to-b after:from-black after:to-transparent after:z-10"
            style={{
              background:
                'url(/images/arcade/arcade-floor.jpg) top center / 1875px 1279px',
              transform:
                'translateZ(100vw) translateY(calc(-50% + 1vw)) translateX(-50%) rotateX(88.7deg) scale(2)'
            }}
          />

          {/* Characters */}

          <CharacterGroup side="L" amount={3} />
          <CharacterGroup side="R" amount={3} />
        </motion.div>
      </div>

      {/* MAIN CONTENT */}

      <div
        ref={scenePlaceholderRef}
        className={classNames({ 'opacity-0': !hasMeasurements })}
      >
        {/* LOGO */}
        <div className="sticky top-0 w-full mx-auto">
          <div className="h-[80vh] lg:h-[65vh] flex flex-col justify-center">
            <div className="absolute inset-0">
              <Neon className="h-full w-full" />
            </div>
            <div style={{ perspective: 3000, transformStyle: 'preserve-3d' }}>
              <motion.div
                className="max-w-[858px] mx-auto mt-[15vh] md:mt-[5vh] lg:mt-[20vh]"
                style={{
                  scale: logoScale
                }}
              >
                <SeasonStartCountdownBadge className="mb-4" season={season} />
                <div className="max-w-[620px] mx-auto aspect-[1425/513] relative">
                  <Image
                    alt="logo"
                    src={'/images/arcade/arcade-logo.webp'}
                    layout="fill"
                  />
                </div>
                <div className="h3 flex flex-wrap w-full justify-center gap-1 text-[1.95vw] -mt-[.5vw]">
                  <motion.div
                    animate={isTitle1Hidden ? 'hidden' : 'visible'}
                    variants={LOGO_TITLE_VARIANTS}
                    initial="hidden"
                  >
                    Collect.{' '}
                  </motion.div>
                  <motion.div
                    animate={isTitle2Hidden ? 'hidden' : 'visible'}
                    initial="hidden"
                    variants={LOGO_TITLE_VARIANTS}
                  >
                    Convert.{' '}
                  </motion.div>
                  <motion.div
                    animate={isTitle3Hidden ? 'hidden' : 'visible'}
                    initial="hidden"
                    variants={LOGO_TITLE_VARIANTS}
                  >
                    Compete.
                  </motion.div>
                </div>
                <div className="relative">
                  {/* Button Content */}
                  <motion.div
                    animate={areActionsHidden ? 'end' : 'start'}
                    initial="start"
                    variants={ACTIONS_VARIANTS}
                    className="mt-3 space-y-4"
                  >
                    <p className="body-lg text-center">
                      Convert Digital Pop! into Arcade Points and compete to
                      stay at or above Rank{' '}
                      <Icon
                        name="trophy"
                        className="inline-block text-arcadeGreen w-[20px] h-[20px]"
                      />{' '}
                      333 to receive exclusive digital & physical collectibles.
                    </p>
                    <div className="flex max-lg:flex-col justify-center items-center gap-2">
                      <ButtonLink
                        className="w-max"
                        size="lg"
                        href={primaryButton.href}
                        theme={primaryButton.theme}
                      >
                        {primaryButton.label}
                      </ButtonLink>
                      <ButtonLink
                        className="w-max"
                        size="lg"
                        href={`#${OVERVIEW_PAGE_SECTION_ID.HOW_TO_PLAY}`}
                        theme="secondary"
                      >
                        How to Play
                      </ButtonLink>
                      <div className="flex gap-2">
                        <CircleIconLink
                          name="discord"
                          url={SOCIAL_LINKS.discord}
                        />
                        <SocialShareMenu />
                      </div>
                    </div>
                  </motion.div>

                  {/* Scroll Arrow */}
                  <motion.div
                    key="arrow"
                    animate={isArrowHidden ? 'end' : 'start'}
                    initial="start"
                    variants={SCROLL_ARROW_VARIANTS}
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-4 mx-auto"
                  >
                    <Link
                      href={`#${OVERVIEW_PAGE_SECTION_ID.HOW_TO_PLAY}`}
                      className="before:content-[''] before:absolute before:-inset-4"
                    >
                      <Icon
                        name="scrollArrow"
                        className="animate-bounce w-full h-full"
                      />
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* SPACE CREATOR */}
        <div className="h-[100vh] md:h-[120vh] lg:h-[140vh] pointer-events-none" />
      </div>
    </div>
  )
}

export default ArcadeOverviewHero
